import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const formatCurrency = (val) => {
  if (val == null) return "-";
  return `₹${Number(val).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

export default function PayrollSummary({ data, onDrillDown }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const summary = data?.payrollSummary;
  const branchWise = summary?.branchWise || [];

  const chartData = branchWise.map((b) => ({
    name: b.branch,
    "Gross Pay": Math.round(b.grossPay || 0),
    "Net Pay": Math.round(b.netPay || 0),
    "Shift Paisa": Math.round(b.shiftPaisaAmount || 0),
  }));

  const statCards = [
    {
      label: "Total Gross",
      value: formatCurrency(summary?.totalGrossPay),
      color: "#1565C0",
      bg: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
    },
    {
      label: "Total Net",
      value: formatCurrency(summary?.totalNetPay),
      color: "#2E7D32",
      bg: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
    },
    {
      label: "Total Deductions",
      value: formatCurrency(summary?.totalDeductions),
      color: "#D32F2F",
      bg: "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
    },
    {
      label: "Total Shift Paisa",
      value: formatCurrency(summary?.totalShiftPaisa),
      color: "#6A1B9A",
      bg: "linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)",
    },
  ];

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "16px",
        p: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        height: "100%",
      }}
    >
      <Typography
        sx={{ fontWeight: 700, fontSize: "16px", color: "#333", mb: 1.5 }}
      >
        Payroll Summary
        {summary?.employeeCount != null && (
          <Typography
            component="span"
            sx={{ fontSize: "12px", color: "#888", ml: 1 }}
          >
            ({summary.employeeCount} employees)
          </Typography>
        )}
      </Typography>

      {/* Mini stat cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 1.5,
          mb: 2,
        }}
      >
        {statCards.map((s, i) => (
          <Box
            key={i}
            onClick={() => onDrillDown && onDrillDown(null)}
            sx={{
              background: s.bg,
              borderRadius: "12px",
              p: 1.5,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
            }}
          >
            <Typography sx={{ fontSize: "11px", color: "#666" }}>
              {s.label}
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 700, color: s.color }}
            >
              {s.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Branch-wise chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={isSmall ? 180 : 220}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
            layout="vertical"
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: "#666" }} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: isSmall ? 10 : 12, fill: "#555" }}
              width={isSmall ? 60 : 80}
            />
            <Tooltip
              formatter={(val) => formatCurrency(val)}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Gross Pay" fill="#1565C0" barSize={12} radius={[0, 4, 4, 0]} onClick={(d) => onDrillDown && onDrillDown(d?.name)} style={{ cursor: "pointer" }} />
            <Bar dataKey="Net Pay" fill="#2E7D32" barSize={12} radius={[0, 4, 4, 0]} onClick={(d) => onDrillDown && onDrillDown(d?.name)} style={{ cursor: "pointer" }} />
            <Bar dataKey="Shift Paisa" fill="#6A1B9A" barSize={12} radius={[0, 4, 4, 0]} onClick={(d) => onDrillDown && onDrillDown(d?.name)} style={{ cursor: "pointer" }} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography textAlign="center" color="textSecondary" sx={{ py: 4 }}>
          No payroll data available for this month.
        </Typography>
      )}
    </Box>
  );
}
